import { gql, type TypedDocumentNode } from '@apollo/client';
import { type PropsWithChildren } from 'react';

import { HeartIcon } from '@sorare/core/src/atoms/icons/HeartIcon';
import { timeKeyFromProgress } from '@sorare/core/src/atoms/icons/colorsByTime';
import { range } from '@sorare/core/src/lib/arrays';
import type { Unit } from '@sorare/core/src/lib/style';

import type { Lifes_thresholdPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: Lifes_thresholdPickerTask;
  size?: Unit;
};

export const Lifes = ({ task, size = 2 }: PropsWithChildren<Props>) => {
  const {
    maxLineupsCount,
    pendingLineupsCount,
    lockedLineupsCount,
    remainingLineupsCount,
    expiresAt,
    wouldExpireAt,
    expirationProgressBasisPoints,
  } = task;
  // 25 by default to keep the same color for WC and set
  const time = !expiresAt
    ? 'locked'
    : expirationProgressBasisPoints !== null
      ? timeKeyFromProgress(expirationProgressBasisPoints)
      : '25';

  const getStatus = (index: number) => {
    if (index < remainingLineupsCount) {
      return 'open';
    }
    if (index < remainingLineupsCount + lockedLineupsCount) {
      return 'locked';
    }
    if (index < pendingLineupsCount + remainingLineupsCount) {
      return 'editable';
    }
    return 'closed';
  };

  return (
    <>
      {range(maxLineupsCount).map(i => (
        <HeartIcon
          key={i}
          status={getStatus(i)}
          size={size}
          time={
            expiresAt || wouldExpireAt
              ? expirationProgressBasisPoints !== null
                ? time
                : 'locked'
              : undefined
          }
        />
      ))}
    </>
  );
};

Lifes.fragments = {
  thresholdPickerTask: gql`
    fragment Lifes_thresholdPickerTask on ThresholdPickerTask {
      id
      maxLineupsCount
      pendingLineupsCount
      lockedLineupsCount
      remainingLineupsCount
      expiresAt
      wouldExpireAt
      expirationProgressBasisPoints
    }
  ` as TypedDocumentNode<Lifes_thresholdPickerTask>,
};
